import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { getTwoFactorStatus, updateTwoFactorStatus } from '../../services/systemSettingsService';

const TwoFactorAuthModule = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin; // Only admins can manage system-wide settings
  
  const [systemEnabled, setSystemEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updating, setUpdating] = useState(false);

  // Fetch 2FA settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError('');
      
      try {
        const systemResponse = await getTwoFactorStatus();
        setSystemEnabled(systemResponse.enabled);
      } catch (err) {
        console.error('Error fetching 2FA settings:', err);
        // Don't set an error message for authentication issues
        if (!err.message.includes('authenticate')) {
          setError('Failed to load 2FA settings. Please try again later.');
        }
        // Default to disabled
        setSystemEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  // Handle system-wide 2FA toggle (admin only)
  const handleSystemToggle = async () => {
    if (!isAdmin) return;
    
    setUpdating(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await updateTwoFactorStatus(!systemEnabled);
      
      setSystemEnabled(response.enabled);
      setSuccess(`Two-factor authentication has been ${response.enabled ? 'enabled' : 'disabled'} system-wide.`);
    } catch (err) {
      // Check if it's an authentication error
      if (err.message.includes('authenticate')) {
        setError('Your session has expired. Please refresh the page and log in again.');
      } else {
        setError(err.message || 'Failed to update system 2FA setting.');
      }
      console.error('Error updating system 2FA setting:', err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Typography variant="h6" gutterBottom>
          Two-Factor Authentication
        </Typography>
        
        <Typography variant="body2" color="textSecondary" paragraph>
          Enhance login security by requiring a verification code sent via email
          in addition to passwords. This adds an extra layer of protection for user accounts
          and helps prevent unauthorized access even if passwords are compromised.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {canManage ? (
          <Box display="flex" alignItems="center" mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={systemEnabled}
                  onChange={handleSystemToggle}
                  disabled={updating}
                />
              }
              label={`Two-factor authentication is ${systemEnabled ? 'enabled' : 'disabled'} system-wide`}
            />
            {updating && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Box>
        ) : (
          <Typography variant="body1" color="textSecondary">
            Two-factor authentication is currently {systemEnabled ? 'enabled' : 'disabled'} system-wide.
            Only administrators can change this setting.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorAuthModule;
